import React, { useCallback, useMemo, useState } from 'react';
import Chart from 'react-apexcharts';
import dayjs from 'dayjs';
import { useLanguage } from '../../context/LanguageContext';
import { amountDisplay, isDarkModeOn } from '../../util/helper';

const formatData = (data) => {
    if (!data || typeof data !== 'object') {
        return {};
    }

    const result = {};

    Object.keys(data).forEach((date) => {
        const dayData = data[date];
        if (!dayData || typeof dayData !== 'object') {
            return;
        }

        Object.entries(dayData).forEach(([category, value]) => {
            if (!result[category]) {
                result[category] = [];
            }

            result[category].push(Math.abs(value));
        });
    });

    return Object.entries(result).reduce((acc, [category, values]) => {
        if (values.some((value) => value !== 0)) {
            acc[category] = values;
        }

        return acc;
    }, {});
};

const colors = [
    '#f10d5d', // Yellow
    '#AF7CFF', // Purple
    '#FF6F91', // Pinkish Red
    '#845EC2', // Deep Purple
    '#4ff6de', // Teal
    '#4D8076', // Slate Green
    '#C34A36', // Brick Red
    '#FFC75F', // Soft Yellow
    '#7173f9', // Lemon Yellow
    '#00C9A7', // Aqua
    '#FF9671', // Light Coral
    '#B39CD0', // Lavender
    '#FF6F61', // Tomato
    '#0081CF', // Cerulean
    '#6B4226', // Coffee Brown
    '#C34A36', // Burnt Sienna
    '#198754', // Bootstrap Green
    '#6FE79F', // Mint Green
    '#FF8F3F', // Orange
    '#06BFFA', // Light Blue
];

const CategoryDonutChart = ({
    categoryExpense = [],
    data = null,
    daysInMonth = null,
    month,
    language,
    reportingType = 'monthly',
}) => {
    const { t } = useLanguage();
    const [selectedCategory, setSelectedCategory] = useState(null);
    const isDark = isDarkModeOn();
    const isMonthly = reportingType === 'monthly';
    const textColor = isDark ? '#e2e8f0' : '#373e3c';
    const labelColor = isDark ? '#9ba3aa' : '#958f9f';
    const periodDate = month ? dayjs(month) : dayjs();
    const categoryData = useMemo(() => formatData(data), [data]);
    const monthTitle =
        reportingType === 'yearly'
            ? periodDate.format('YYYY')
            : month
              ? periodDate.format('MMM YYYY')
              : t('thisMonth');
    const labels = useMemo(
        () => categoryExpense.map((item) => item.category_name),
        [categoryExpense]
    );

    const handleDonutClick = useCallback(
        (event, chartContext, config) => {
            const index = config.dataPointIndex;
            const category = labels[index];
            if (!category) {
                return;
            }

            setSelectedCategory((currentSelectedCategory) =>
                currentSelectedCategory === category ? null : category
            );
        },
        [labels]
    );

    const series = useMemo(() => {
        return categoryExpense.map((item) => Number(item.total_amount || 0));
    }, [categoryExpense]);

    const options = useMemo(() => {
        return {
            chart: {
                type: 'donut',
                height: 400,
                background: 'transparent',
                fontFamily: 'Lato, sans-serif',
                toolbar: { show: false },
                animations: {
                    enabled: true,
                    easing: 'easeinout',
                    speed: 700,
                },
                events: isMonthly
                    ? {
                          dataPointSelection: handleDonutClick,
                      }
                    : {},
            },
            labels,
            colors,
            dataLabels: {
                enabled: false,
            },
            stroke: {
                width: isMonthly && selectedCategory ? 1 : 3,
                colors: [isDark ? '#17161b' : '#ffffff'],
            },
            fill: {
                type: 'gradient',
                gradient: {
                    shade: 'light',
                    type: 'vertical',
                    gradientToColors: undefined,
                    opacityFrom: 0.95,
                    opacityTo: 1,
                },
            },
            states: {
                normal: {
                    filter: { type: 'none' },
                },
                hover: {
                    filter: { type: 'lighten', value: 0.05 },
                },
                active: {
                    filter: { type: 'darken', value: 0.15 },
                },
            },
            legend: {
                position: 'bottom',
                fontFamily: 'Lato',
                fontSize: '13px',
                fontWeight: 600,
                labels: {
                    colors: textColor,
                },
                markers: {
                    width: 10,
                    height: 10,
                    radius: 5,
                },
                itemMargin: {
                    horizontal: 8,
                    vertical: 4,
                },
            },
            responsive: [
                {
                    breakpoint: 768,
                    options: {
                        legend: {
                            position: 'bottom',
                            fontSize: '11px',
                            fontWeight: 600,
                        },
                    },
                },
                {
                    breakpoint: 576,
                    options: {
                        chart: { height: 340 },
                        legend: {
                            position: 'left',
                            fontSize: '10px',
                            fontWeight: 500,
                        },
                    },
                },
            ],
            plotOptions: {
                pie: {
                    donut: {
                        size: '62%',
                        labels: {
                            show: true,
                            total: {
                                show: true,
                                label:
                                    isMonthly && selectedCategory ? selectedCategory : t('total'),
                                fontSize: '12px',
                                fontWeight: 600,
                                color: labelColor,
                                formatter() {
                                    if (isMonthly && selectedCategory) {
                                        const selectedIndex = labels.indexOf(selectedCategory);
                                        return `${amountDisplay(series[selectedIndex] || 0, language)}`;
                                    }

                                    return `${amountDisplay(
                                        series.reduce((a, b) => a + b, 0),
                                        language
                                    )}`;
                                },
                            },
                            value: {
                                fontSize: '18px',
                                fontWeight: 700,
                                color: textColor,
                            },
                        },
                    },
                },
            },
            tooltip: {
                theme: isDark ? 'dark' : 'light',
                y: {
                    formatter: (val) => `${amountDisplay(val, language)}`,
                },
            },
        };
    }, [
        handleDonutClick,
        isDark,
        isMonthly,
        labelColor,
        labels,
        language,
        selectedCategory,
        series,
        t,
        textColor,
    ]);

    const barChartOptions = useMemo(
        () => ({
            chart: {
                type: 'bar',
                height: 350,
                background: 'transparent',
                fontFamily: 'Lato, sans-serif',
                toolbar: { show: false },
                animations: {
                    enabled: true,
                    easing: 'easeinout',
                    speed: 700,
                },
            },
            colors: [colors[Math.max(labels.indexOf(selectedCategory), 0)]],
            xaxis: {
                categories: Array.from({ length: daysInMonth || 0 }, (_, i) => `Day ${i + 1}`),
                axisBorder: { show: false },
                axisTicks: { show: false },
                labels: {
                    show: true,
                    rotate: -45,
                    style: {
                        fontSize: '9px',
                        fontFamily: 'Lato',
                        colors: labelColor,
                    },
                },
                tickAmount: 5,
            },
            yaxis: {
                labels: {
                    formatter: (val) => `${amountDisplay(val, language)}`,
                    style: {
                        fontSize: '10px',
                        colors: labelColor,
                    },
                },
            },
            grid: {
                strokeDashArray: 4,
                borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(149,143,159,0.18)',
            },
            plotOptions: {
                bar: {
                    horizontal: false,
                    borderRadius: 6,
                    columnWidth: '35%',
                },
            },
            dataLabels: {
                enabled: false,
            },
            tooltip: {
                theme: isDark ? 'dark' : 'light',
                y: {
                    formatter: (val) => `${amountDisplay(val, language)}`,
                },
            },
        }),
        [daysInMonth, isDark, labelColor, labels, language, selectedCategory]
    );

    return (
        <section>
            <div className="custom-container">
                <div className="chart">
                    <div className="d-flex justify-content-between">
                        <h3 className="fw-semibold dark-text">
                            {t('expense')} -{' '}
                            <span className="light-text fw-normal">{monthTitle}</span>
                        </h3>
                        <span className="badge text-bg-light">
                            {reportingType === 'yearly' ? t('yearly') : t('monthly')}
                        </span>
                    </div>

                    <Chart options={options} series={series} type="donut" height={400} />

                    {isMonthly &&
                        selectedCategory &&
                        Array.isArray(categoryData[selectedCategory]) && (
                            <div className="pt-4">
                                <h5 className="fw-semibold dark-text">
                                    {selectedCategory} |{' '}
                                    <span className="light-text fw-normal">Date-wise</span>
                                </h5>
                                <Chart
                                    options={barChartOptions}
                                    series={[
                                        {
                                            name: selectedCategory,
                                            data: categoryData[selectedCategory],
                                        },
                                    ]}
                                    type="bar"
                                    height={200}
                                />
                            </div>
                        )}
                </div>
            </div>
        </section>
    );
};

export default CategoryDonutChart;
